import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { AuthLayout } from '../../components/AuthLayout';
import { Checkbox } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import { Alert, AlertDescription } from '@werkstatt/ui';
import { readApiError, readApiErrorCode } from '../../lib/apiError';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login({
        email, password, remember_me: rememberMe,
        totp_code: needsTotp ? totpCode : undefined,
      });
      const next = searchParams.get('next') || '/dashboard';
      navigate(next, { replace: true });
    } catch (err) {
      if (readApiErrorCode(err) === 'totp_required') {
        setNeedsTotp(true);
      } else {
        setError(readApiError(err, 'Anmeldung fehlgeschlagen'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Anmelden"
      subtitle="Melden Sie sich an, um auf RentalHub zuzugreifen"
      footer={
        <p>
          Noch kein Konto?{' '}
          <a
            href="https://werkstatt-connect.de/apps/mietwagen"
            className="text-primary hover:text-primary/80 font-medium"
          >
            RentalHub abonnieren
          </a>
        </p>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div>
            <Label htmlFor="email">E-Mail</Label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 block w-full px-3 py-2 border border-border rounded-lg shadow-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
              placeholder="you@example.com"
              autoComplete="email"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label htmlFor="password">Passwort</Label>
              <Link
                to="/auth/forgot-password"
                className="text-xs font-medium text-primary hover:text-primary/80"
              >
                Passwort vergessen?
              </Link>
            </div>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full px-3 py-2 border border-border rounded-lg shadow-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="remember"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(checked === true)}
            />
            <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
              Angemeldet bleiben (30 Tage)
            </Label>
          </div>

          {needsTotp && (
            <div>
              <Label htmlFor="totp-code">2FA-Code</Label>
              <input
                id="totp-code"
                required
                autoFocus
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                className="mt-1.5 block w-full px-3 py-2 border border-border rounded-lg shadow-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                placeholder="123456 oder Backup-Code"
              />
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Wird angemeldet…' : needsTotp ? 'Bestätigen' : 'Anmelden'}
        </button>
      </form>
    </AuthLayout>
  );
}
